import { useEffect, useRef } from 'react';
import {
  collectFormDataExtraFields,
  getFormValuesFromExtraFields,
  recordTypeFormKey,
} from '../logic';
import { useForm } from 'react-hook-form';
import { isEqual } from 'lodash';
import * as yup from 'yup';
import { ExtraFieldInternalValue } from '../extraFieldInternalValue';
import { AvailableRecordTypeFragment } from '../../../__generated__/graphql';

export type Props = {
  onChange: (
    extraFields: ExtraFieldInternalValue[],
    recordTypeName: string | undefined,
  ) => void;
  values?: readonly ExtraFieldInternalValue[];
  recordTypeName: string | null; // null means no recordType
  availableRecordTypes: readonly AvailableRecordTypeFragment[];
  validationSchema?: yup.ObjectSchema<any>;
};

export const useExtraFieldsFormSimple = ({
  values = [],
  recordTypeName,
  onChange,
  validationSchema,
  availableRecordTypes,
}: Props) => {
  const defaultRecordTypeName =
    availableRecordTypes.find((x) => x.IsDefault)?.DeveloperName ?? 'Master';
  const formValues = getFormValuesFromExtraFields(
    values,
    recordTypeName ?? defaultRecordTypeName,
  );
  // we need to store last properties to understand then we need to reset
  const initialFormValues = useRef<Record<string, any>>(formValues);
  // reset in progress flag
  const resetingToValues = useRef<Record<string, any> | undefined>(undefined);

  const formMethods = useForm({
    defaultValues: initialFormValues.current,
    validationSchema,
  });

  const { watch, reset } = formMethods;

  const watchedValues = watch();

  // we always need to call form.reset if values were changed externally. Because 'react-hook-form' has some internal state...
  // resetingToValues flag used to avoid onChange event during the reset
  useEffect(() => {
    if (!isEqual(formValues, initialFormValues.current)) {
      initialFormValues.current = formValues;
      resetingToValues.current = formValues;
      reset(formValues);
    }
  }, [reset, formValues]);

  useEffect(() => {
    if (
      resetingToValues.current !== undefined &&
      !isEqual(watchedValues, resetingToValues.current)
    ) {
      // form is not been reset yet, we can't raise onChange event
      return;
    }
    resetingToValues.current = undefined;
    if (!isEqual(watchedValues, formValues)) {
      initialFormValues.current = watchedValues; // we need this to not call reset second time
      const extraFieldInputs = collectFormDataExtraFields(watchedValues);
      const recordTypeName = watchedValues[recordTypeFormKey];
      onChange(extraFieldInputs, recordTypeName);
    }
  }, [watchedValues, onChange, formValues]);

  return { formMethods };
};
